import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { FeatherX, FeatherArrowRight } from '../ui/icons';
import { workPlanApi } from '../../services/api';
import './WorkDayEvaluationModal.css';

const WorkDayEvaluationModal = ({ 
  evaluation, 
  workPlanId,
  formTasksData,
  onComplete, 
  onCancel 
}) => {
  const [conversations, setConversations] = useState([]); // Array of { taskId, messages: [{role, content, timestamp}] }
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [currentInput, setCurrentInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isAutoClosing, setIsAutoClosing] = useState(false); // Track if auto-close is in progress
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasLoadedFirstQuestion = useRef(false); // Track if first question has been loaded
  const hasAutoClosed = useRef(false); // Prevent duplicate auto-close calls

  // Initialize conversations when evaluation changes
  useEffect(() => {
    if (evaluation?.problematicTasks && evaluation.problematicTasks.length > 0) {
      // Reset the flag when evaluation changes (new modal open)
      hasLoadedFirstQuestion.current = false;
      
      console.log('[WorkDayEvaluation] Initializing conversations for', evaluation.problematicTasks.length, 'tasks');
      const initialConversations = evaluation.problematicTasks.map(task => ({
        taskId: task.taskId,
        taskTitle: task.taskTitle,
        messages: []
      }));
      setConversations(initialConversations);
      setCurrentTaskId(initialConversations[0]?.taskId);
      
      // Auto-load first question immediately (only once per evaluation)
      const firstTaskId = initialConversations[0]?.taskId;
      if (firstTaskId && !hasLoadedFirstQuestion.current) {
        console.log('[WorkDayEvaluation] Loading first question for task:', firstTaskId);
        hasLoadedFirstQuestion.current = true; // Mark as loaded BEFORE calling
        loadFirstQuestion(firstTaskId);
      }
    }
  }, [evaluation?.problematicTasks?.length]); // Only depend on the length, not the whole object

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversations]);

  // Auto-close when discussion is complete
  useEffect(() => {
    if (isComplete && !hasAutoClosed.current) {
      console.log('[WorkDayEvaluation] Discussion complete - auto-closing work plan');
      hasAutoClosed.current = true;
      setIsAutoClosing(true);
      handleComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  // Focus input when new question arrives
  useEffect(() => {
    if (!isLoading && currentInput === '') {
      inputRef.current?.focus();
    }
  }, [isLoading, conversations]);

  const loadFirstQuestion = async (taskId) => {
    if (!taskId) {
      console.warn('[WorkDayEvaluation] loadFirstQuestion called without taskId');
      return;
    }
    
    // Check if question already exists for this task - use a more reliable check
    const currentConv = conversations.find(conv => conv.taskId === taskId);
    if (currentConv && currentConv.messages.some(m => m.role === 'bot')) {
      console.log('[WorkDayEvaluation] First question already exists for task', taskId, '- skipping duplicate');
      return;
    }
    
    // Additional check: if we're already loading, don't start another load
    if (isLoading) {
      console.log('[WorkDayEvaluation] Already loading, skipping duplicate call');
      return;
    }
    
    console.log('[WorkDayEvaluation] Loading first question for task:', taskId);
    setIsLoading(true);
    try {
      const task = evaluation.problematicTasks?.find(t => t.taskId === taskId);
      const taskTitle = task?.taskTitle || 'this task';
      
      console.log('[WorkDayEvaluation] Calling API with empty conversationHistory for first question');
      const response = await workPlanApi.getNextQuestion(workPlanId, {
        taskId,
        conversationHistory: [],
        currentTaskIndex: currentTaskIndex,
        tasks: formTasksData || [] // Pass form data for accurate hour calculations
      });

      // Use the question directly - AI should already include task context naturally
      let firstMessage = response.question;
      if (response.taskContext) {
        firstMessage = `${response.taskContext}\n\n${response.question}`;
      }

      // Double-check before adding message to prevent duplicates
      // Use a function updater to get the latest state
      setConversations(prev => {
        const updatedConv = prev.find(conv => conv.taskId === taskId);
        if (updatedConv && updatedConv.messages.some(m => m.role === 'bot')) {
          console.log('[WorkDayEvaluation] Question already exists in conversation - skipping duplicate');
          return prev; // Return unchanged state
        }
        
        // Add the message
        return prev.map(conv => {
          if (conv.taskId === taskId) {
            return {
              ...conv,
              messages: [...conv.messages, {
                role: 'bot',
                content: firstMessage,
                timestamp: new Date().toISOString()
              }]
            };
          }
          return conv;
        });
      });
    } catch (error) {
      console.error('Error loading first question:', error);
      const task = evaluation.problematicTasks?.find(t => t.taskId === taskId);
      const taskTitle = task?.taskTitle || 'this task';
      const daysText = task?.days === 1 ? '1 day' : `${task?.days || 0} days`;
      const hoursText = task?.totalHours > 0 ? `over ${Math.round(task.totalHours)} hours` : '';
      updateConversation(taskId, {
        role: 'bot',
        content: `Why is "${taskTitle}" taking so long? I believe you've been on it for almost ${daysText} on and off${hoursText ? `, and already spent ${hoursText}` : ''}.`,
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateConversation = (taskId, message) => {
    setConversations(prev => prev.map(conv => {
      if (conv.taskId === taskId) {
        // Check for duplicates before adding
        const isDuplicate = conv.messages.some(m => 
          m.role === message.role && 
          m.content === message.content &&
          Math.abs(new Date(m.timestamp) - new Date(message.timestamp)) < 1000 // Within 1 second
        );
        
        if (isDuplicate) {
          console.log('[WorkDayEvaluation] Duplicate message detected, skipping:', message.content.substring(0, 50));
          return conv; // Return unchanged
        }
        
        return {
          ...conv,
          messages: [...conv.messages, message]
        };
      }
      return conv;
    }));
  };

  const getCurrentConversation = () => {
    return conversations.find(conv => conv.taskId === currentTaskId);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!currentInput.trim() || isSending || isLoading) return;

    const userMessage = currentInput.trim();
    setCurrentInput('');
    setIsSending(true);

    try {
      const currentConv = getCurrentConversation();
      
      // Check if there's already a bot message - if so, this is an answer, not a first question
      const hasBotMessage = currentConv.messages.some(m => m.role === 'bot');
      
      console.log('[WorkDayEvaluation] ===== HANDLE SEND =====');
      console.log('[WorkDayEvaluation] Current conversation:', currentConv);
      console.log('[WorkDayEvaluation] Has bot message:', hasBotMessage);
      console.log('[WorkDayEvaluation] Messages:', currentConv.messages.map(m => ({ role: m.role, content: m.content.substring(0, 50) })));
      
      if (hasBotMessage) {
        console.log('[WorkDayEvaluation] ✅ Bot message exists - user is answering. Building conversation history...');
        
        // Add user message to UI first
        updateConversation(currentTaskId, {
          role: 'user',
          content: userMessage,
          timestamp: new Date().toISOString()
        });

        // Wait for state to update
        await new Promise(resolve => setTimeout(resolve, 150));
        
        const updatedConv = getCurrentConversation();
        
        // Build conversation history from ALL messages
        const updatedHistory = [];
        for (let i = 0; i < updatedConv.messages.length - 1; i++) {
          const currentMsg = updatedConv.messages[i];
          const nextMsg = updatedConv.messages[i + 1];
          
          if (currentMsg.role === 'bot' && nextMsg.role === 'user') {
            updatedHistory.push({
              question: currentMsg.content,
              answer: nextMsg.content
            });
          }
        }
        
        console.log('[WorkDayEvaluation] ✅ User answered. Conversation history:', updatedHistory);
        console.log('[WorkDayEvaluation] Ending conversation - ONE question only');
        
        // Add a short thank you message with encouragement
        const thankYouMessages = [
          "Thanks for the update. Keep pushing forward!",
          "Appreciate the update. You've got this!",
          "Thanks for sharing. Keep up the good work!",
          "Got it. Stay focused and you'll get there!",
          "Thanks. Keep going strong!"
        ];
        const randomMessage = thankYouMessages[Math.floor(Math.random() * thankYouMessages.length)];
        
        // Wait a moment, then add thank you message
        await new Promise(resolve => setTimeout(resolve, 300));
        
        updateConversation(currentTaskId, {
          role: 'bot',
          content: randomMessage,
          timestamp: new Date().toISOString()
        });
        
        // User has answered - end conversation after showing thank you
        setIsSending(false);
        setTimeout(() => {
          moveToNextTask();
        }, 1500); // Give time to see the thank you message
        return;
      }
      
      // If no bot message exists, this shouldn't happen (first question should be loaded automatically)
      // But as a safety measure, add the user message and don't call API
      console.warn('[WorkDayEvaluation] ⚠️ No bot message found. This is unexpected.');
      console.warn('[WorkDayEvaluation] Current conversation messages:', currentConv.messages);
      updateConversation(currentTaskId, {
        role: 'user',
        content: userMessage,
        timestamp: new Date().toISOString()
      });
      setIsSending(false);
      return;
    } catch (error) {
      console.error('Error sending message:', error);
      updateConversation(currentTaskId, {
        role: 'bot',
        content: 'I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsSending(false);
    }
  };

  const moveToNextTask = () => {
    const nextIndex = currentTaskIndex + 1;
    if (nextIndex < conversations.length) {
      setCurrentTaskIndex(nextIndex);
      setCurrentTaskId(conversations[nextIndex].taskId);
      loadFirstQuestion(conversations[nextIndex].taskId);
    } else {
      // All tasks completed
      setIsComplete(true);
    }
  };

  const handleComplete = async () => {
    try {
      // Prepare evaluation data
      const evaluationData = {
        evaluationData: {
          summary: evaluation.summary,
          problematicTasks: conversations.map(conv => ({
            taskId: conv.taskId,
            taskTitle: conv.taskTitle,
            conversation: conv.messages
          }))
        },
        aiQuestions: conversations.flatMap(conv => 
          conv.messages.filter(m => m.role === 'bot').map(m => m.content)
        ),
        userResponses: conversations.flatMap(conv => {
          const botMessages = conv.messages.filter(m => m.role === 'bot');
          const userMessages = conv.messages.filter(m => m.role === 'user');
          return botMessages.map((botMsg, index) => ({
            question: botMsg.content,
            response: userMessages[index]?.content || ''
          }));
        })
      };

      await workPlanApi.saveEvaluation(workPlanId, evaluationData);
      onComplete();
    } catch (error) {
      console.error('Error completing discussion:', error);
      setIsAutoClosing(false);
      hasAutoClosed.current = false; // Allow retry on error
    }
  };

  if (!evaluation || !evaluation.hasIssues) {
    return null;
  }

  const currentConv = getCurrentConversation();
  const currentTask = evaluation.problematicTasks?.find(t => t.taskId === currentTaskId);

  return (
    <div 
      className="workday-evaluation-modal-overlay" 
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSending && !isLoading) {
          onCancel();
        }
      }}
    >
      <div className="workday-evaluation-chat-container" onClick={(e) => e.stopPropagation()}>
        <div className="workday-evaluation-chat-header">
          <div className="workday-evaluation-chat-header-content">
            <div className="workday-evaluation-chat-avatar">M</div>
            <div>
              <h2>Manager Connect</h2>
              {currentTask && (
                <p className="workday-evaluation-chat-subtitle">
                  {currentTask.taskTitle} • Task {currentTaskIndex + 1} of {conversations.length}
                </p>
              )}
            </div>
          </div>
          <button 
            className="workday-evaluation-chat-close-btn"
            onClick={onCancel}
            disabled={isSending || isLoading}
          >
            <FeatherX size={20} />
          </button>
        </div>

        <div className="workday-evaluation-chat-messages" ref={messagesEndRef}>
          {currentConv?.messages.map((msg, index) => (
            <div
              key={index}
              className={`workday-evaluation-message ${msg.role === 'bot' ? 'bot' : 'user'}`}
            >
              <div className="workday-evaluation-message-content">
                {msg.content}
              </div>
              <div className="workday-evaluation-message-time">
                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
          {(isLoading || isSending) && (
            <div className="workday-evaluation-message bot">
              <div className="workday-evaluation-message-content">
                <div className="workday-evaluation-typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>

        {isComplete ? (
          <div className="workday-evaluation-chat-complete">
            <div className="workday-evaluation-auto-closing">
              <p>Thanks for the update!</p>
              <div className="workday-evaluation-loading-message">
                <div className="workday-evaluation-typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <p>Wrapping up and closing work plan...</p>
              </div>
            </div>
          </div>
        ) : currentConv?.messages.length > 0 && currentConv.messages[currentConv.messages.length - 1]?.role === 'bot' ? (
          <form className="workday-evaluation-chat-input-form" onSubmit={handleSend}>
            <input
              ref={inputRef}
              type="text"
              className="workday-evaluation-chat-input"
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              placeholder="Type your answer..."
              disabled={isSending || isLoading}
            />
            <button
              type="submit"
              className="workday-evaluation-chat-send-btn"
              disabled={!currentInput.trim() || isSending || isLoading}
              title="Send"
            >
              <FeatherArrowRight size={18} />
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
};

export default WorkDayEvaluationModal;
